import { useMemo } from 'react';

import { navigateTo } from 'actions/main';
import {
  sendTrackingEvent,
  setSelectedArticle,
} from 'actions/workflows/workflows';
import { ArticleCarouselComponent, ArticleComponent } from 'api/types';
import AgathaMessage from 'components/AgathaMessage/AgathaMessage';
import {
  ReadArticleLink,
  appendAnalyticsParamsToURL,
} from 'components/Article/Article';
import { ARTICLE_PAGE } from 'constants/pageConsts';
import { useAppDispatch, useAppSelector } from 'hooks';
import React from 'react';
import {
  selectCustomConfigParams,
  selectIsAuthorIconVisible,
} from 'reducers/workflows';
import { selectLegibleElementColor } from 'reducers/config';
import { styled } from '@mui/material/styles';
import Markdown, { type MarkdownToJSX } from 'markdown-to-jsx';
import sanitizeHtml from 'utils/sanitizeHtml';
import { isValidUrl } from 'utils/utils';

interface Props {
  isLastStep: boolean;
  message: string;
  stepIndex: number;
  widgetComponent: ArticleCarouselComponent;
}

export const ArticleTextMessage: React.FC<React.PropsWithChildren<Props>> = ({
  message,
  stepIndex,
  widgetComponent,
}) => {
  const dispatch = useAppDispatch();
  const customConfigParams = useAppSelector(selectCustomConfigParams);
  const legibleElementColor = useAppSelector(selectLegibleElementColor);
  const isAuthorIconVisible = useAppSelector(state =>
    selectIsAuthorIconVisible(state, stepIndex),
  );
  const hideNav = customConfigParams?.hide_nav ?? '';
  const {
    article_step_data: articleStepData,
    forethought_text: forethoughtText,
    knowledge_articles: articles,
    read_article_action: readArticleAction,
  } = widgetComponent.component_fields;
  const readArticleText =
    forethoughtText.related_articles_text || 'Related Articles';
  const openInWidget =
    hideNav !== 'true' && readArticleAction === 'expand_in_widget';

  const markdownOptions: MarkdownToJSX.Options = useMemo(
    () => ({
      overrides: {
        a: {
          props: {
            rel: 'noreferrer',
            target: '_blank',
          },
        },
        code: {
          component: ({
            children,
            className,
          }: {
            children: React.ReactNode;
            className: string;
          }) => {
            return (
              <code className={className}>
                {typeof children === 'string'
                  ? children.replace(/&lt;\?php/g, '<?php')
                  : children}
              </code>
            );
          },
        },
      },
      wrapper: React.Fragment,
    }),
    [],
  );

  function sendArticleTrackingEvent(
    documentID: string,
    documentIndex: number,
    traceId?: string,
    spanId?: string,
    standardized_document_id?: string,
  ) {
    dispatch(
      sendTrackingEvent({
        current_step_id: articleStepData?.current_step_id,
        doc_id: documentID,
        doc_index: documentIndex,
        event_type: 'go-to-article',
        intent_workflow_id: articleStepData?.intent_workflow_id,
        intent_workflow_version: articleStepData?.intent_workflow_version,
        org_id: articleStepData?.org_id,
        span_id: spanId,
        standardized_document_id: standardized_document_id,
        trace_id: traceId,
      }),
    );
  }

  function openArticleInWidget(
    article: ArticleComponent,
    articleIndex: number,
  ) {
    dispatch(setSelectedArticle(article));
    dispatch(navigateTo(ARTICLE_PAGE));
    sendArticleTrackingEvent(
      article.document_id,
      articleIndex,
      article.trace_id,
      article.span_id,
      article.standardized_document_id,
    );
  }

  function getLinks() {
    if (
      readArticleAction === 'none' ||
      (!openInWidget && articles.every(article => !article.link))
    ) {
      return null;
    }

    const validArticles = articles.filter(
      article => !article.link || isValidUrl(article.link),
    );

    return (
      <>
        <br />
        {validArticles ? <span dir='auto'>{readArticleText}</span> : ''}:
        <br />
        {validArticles.map((article, articleIndex) => {
          if (openInWidget) {
            return (
              <StyledReadArticleLink
                aria-label={`${readArticleText}: ${article.title}`}
                as='button'
                data-testid={`read-article-link-${articleIndex}`}
                key={article.document_id}
                onClick={() => {
                  openArticleInWidget(article, articleIndex);
                }}
                themeColor={legibleElementColor}
              >
                {articleIndex + 1}. {article.title}
              </StyledReadArticleLink>
            );
          } else if (article.link) {
            return (
              <StyledReadArticleLink
                aria-label={`${readArticleText}: ${article.title}`}
                data-testid={`read-article-link-${articleIndex}`}
                href={appendAnalyticsParamsToURL(article.link)}
                key={article.document_id}
                onClick={() => {
                  sendArticleTrackingEvent(
                    article.document_id,
                    articleIndex,
                    article.span_id,
                    article.trace_id,
                    article.standardized_document_id,
                  );
                }}
                rel='noreferrer'
                target='_blank'
                themeColor={legibleElementColor}
              >
                {articleIndex + 1}. {article.title}
              </StyledReadArticleLink>
            );
          } else {
            return null;
          }
        })}
      </>
    );
  }

  return (
    <AgathaMessage
      isAuthorIconVisible={isAuthorIconVisible}
      stepIndex={stepIndex}
    >
      <>
        <Markdown dir='auto' options={markdownOptions}>
          {sanitizeHtml(message.replace(/<\?php/g, '&lt;?php'))}
        </Markdown>
        {getLinks()}
      </>
    </AgathaMessage>
  );
};

const StyledReadArticleLink = styled(ReadArticleLink)`
  padding: 0;
  margin-top: 0;
`;
